// frontend/src/components/Auth/RegisterForm.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { registerUser } from '../../services/api';

const RegisterForm = () => {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState(''); // Optional based on backend settings
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== password2) {
      setError("Passwords do not match.");
      setSuccess('');
      return;
    }
    setError('');
    setSuccess('');
    setIsLoading(true);

    const payload = {
      email,
      password1: password,
      password2: password2,
      // Conditionally add username if your backend requires/allows it.
      // Django Allauth's default with email as primary doesn't need username for registration
      // unless ACCOUNT_USERNAME_REQUIRED is True and username is in ACCOUNT_SIGNUP_FIELDS.
      ...(username && { username }), // Add username to payload only if it's not empty
    };

    try {
      await registerUser(payload);
      setSuccess('Registration successful! Please check your email if verification is required, then login.');
      setTimeout(() => navigate('/login'), 3000); // Give time to read success message
    } catch (err) {
      let errorMessages = [];
      if (err && typeof err === 'object') {
        Object.keys(err).forEach(key => {
          const fieldName = key.replace("password1", "Password").replace("password2", "Password confirmation").replace("username", "Username").replace("email", "Email");
          if (Array.isArray(err[key])) {
            errorMessages.push(`${fieldName}: ${err[key].join(' ')}`);
          }
        });
        if (err.detail && errorMessages.length === 0) {
          errorMessages.push(err.detail);
        }
      }
      setError(errorMessages.length > 0 ? errorMessages.join('; ') : 'Registration failed. Please try again.');
      console.error("Registration submit error details:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 bg-white p-8 shadow-xl rounded-lg border border-gray-200">
      {error && <p className="text-red-600 text-sm text-center bg-red-100 p-3 rounded-md">{error}</p>}
      {success && <p className="text-green-600 text-sm text-center bg-green-100 p-3 rounded-md">{success}</p>}
      <div>
        <label htmlFor="register-email" className="block text-sm font-medium leading-6 text-gray-900">
          Email address
        </label>
        <div className="mt-2">
          <input
            id="register-email"
            name="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
            placeholder="you@example.com"
          />
        </div>
      </div>

      {/* Optional Username Field - uncomment if your backend specifically requires username during registration
          AND you have ACCOUNT_USERNAME_REQUIRED = True and 'username' in ACCOUNT_SIGNUP_FIELDS in Django settings.
          By default, with email as primary identifier, username is often optional or not used at registration.
      */}
      {/*
      <div>
        <label htmlFor="register-username" className="block text-sm font-medium leading-6 text-gray-900">
          Username (optional)
        </label>
        <div className="mt-2">
          <input
            id="register-username"
            name="username"
            type="text"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
          />
        </div>
      </div>
      */}

      <div>
        <label htmlFor="register-password" className="block text-sm font-medium leading-6 text-gray-900">
          Password
        </label>
        <div className="mt-2">
          <input
            id="register-password"
            name="password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
          />
        </div>
      </div>

      <div>
        <label htmlFor="register-password2" className="block text-sm font-medium leading-6 text-gray-900">
          Confirm Password
        </label>
        <div className="mt-2">
          <input
            id="register-password2"
            name="password2"
            type="password"
            autoComplete="new-password"
            value={password2}
            onChange={(e) => setPassword2(e.target.value)}
            required
            className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
          />
        </div>
      </div>

      <div>
        <button
          type="submit"
          disabled={isLoading}
          className={`w-full flex justify-center py-2.5 px-5 border border-transparent rounded-lg shadow-sm text-sm font-semibold text-white transition-colors duration-150 ease-in-out ${
            isLoading
              ? 'bg-blue-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
          }`}
        >
          {isLoading ? 'Creating account...' : 'Create Account'}
        </button>
      </div>
    </form>
  );
};

export default RegisterForm;
